import { FormField } from '@/components/FormField';
import { Icon } from '@/components/Icon';
import { ProblemAlert } from '@/components/ProblemAlert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useSigningUpload, type SigningUploadApi, type SigningUploadTarget } from './useSigningUpload';

/**
 * Upload or replace one piece of an app's signing material: Apple files or the Android keystore (platform operators only).
 * The file and the passwords go to Hangar once and are never shown again; Hangar opens the file with them and checks it
 * (against the rest of the app's material, or against the certificate it replaces) before storing.
 */
export function SigningUploadDialog({ projectId, target, onClose }: { projectId: string; target: SigningUploadTarget | null; onClose: () => void }) {
  return (
    <Dialog open={target !== null} onOpenChange={(open) => (open ? null : onClose())}>
      <DialogContent data-testid="signing-upload-dialog" className="max-w-lg" aria-describedby="signing-upload-desc">
        {target ? <UploadBody key={`${target.app.id}:${target.slot.kind}`} projectId={projectId} target={target} onClose={onClose} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function UploadBody({ projectId, target, onClose }: { projectId: string; target: SigningUploadTarget; onClose: () => void }) {
  const s = useSigningUpload(projectId, target, onClose);
  const replacing = target.current !== null;
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void s.submit();
      }}
      className="contents"
    >
      <DialogHeader>
        <DialogTitle>{replacing ? `Replace ${target.slot.label.toLowerCase()}` : `Upload ${target.slot.label.toLowerCase()}`}</DialogTitle>
        <DialogDescription id="signing-upload-desc">
          For <strong className="text-foreground">{target.app.name}</strong>. {target.slot.hint} Hangar encrypts it, never shows it again, and hands it only to the build provider.
        </DialogDescription>
      </DialogHeader>
      <DialogBody className="flex flex-col gap-4">
        <UploadFields s={s} target={target} />
        <ProblemAlert error={s.error} testId="signing-upload-problem" />
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" data-testid="signing-upload-submit" loading={s.pending}>
          <Icon name="upload" size={18} />
          {replacing ? 'Replace' : 'Upload'}
        </Button>
      </DialogFooter>
    </form>
  );
}

function UploadFields({ s, target }: { s: SigningUploadApi; target: SigningUploadTarget }) {
  const { slot } = target;
  return (
    <>
      <FormField id="signing-file" label={slot.fileLabel} required error={s.problem && !s.file ? s.problem : undefined} hint={s.file ? `${s.file.name} · ${Math.ceil(s.file.size / 1024)} KB` : undefined}>
        <Input id="signing-file" data-testid="signing-file" type="file" accept={slot.accept} onChange={(e) => s.setFile(e.target.files?.[0] ?? null)} />
      </FormField>
      {slot.needsKeystore ? <KeystoreFields s={s} /> : null}
      {slot.needsPassword ? (
        <FormField id="signing-password" label="Password of the .p12" hint="Leave empty when the .p12 was exported without one.">
          <Input id="signing-password" data-testid="signing-password" type="password" autoComplete="off" value={s.password} onChange={(e) => s.setPassword(e.target.value)} />
        </FormField>
      ) : null}
      {slot.needsKeyIds ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField id="signing-key-id" label="Key id" required error={s.problem && s.file && /key id/.test(s.problem) ? s.problem : undefined}>
            <Input id="signing-key-id" data-testid="signing-key-id" value={s.keyId} onChange={(e) => s.setKeyId(e.target.value)} className="font-mono sm:text-[13px]" autoComplete="off" spellCheck={false} />
          </FormField>
          <FormField id="signing-issuer-id" label="Issuer id" required error={s.problem && s.file && /issuer id/.test(s.problem) ? s.problem : undefined}>
            <Input id="signing-issuer-id" data-testid="signing-issuer-id" value={s.issuerId} onChange={(e) => s.setIssuerId(e.target.value)} className="font-mono sm:text-[13px]" autoComplete="off" spellCheck={false} />
          </FormField>
        </div>
      ) : null}
      {slot.kind !== 'asc_api_key' ? (
        <div className="flex items-start gap-2.5">
          <Checkbox id="signing-ack" data-testid="signing-ack" checked={s.acknowledge} onCheckedChange={(v) => s.setAcknowledge(v === true)} className="mt-0.5" />
          <Label htmlFor="signing-ack" className="flex-col items-start gap-0.5 text-[13px] font-normal text-muted-foreground">
            <span className="font-medium text-foreground">This is part of a certificate change</span>
            <span>{slot.needsKeystore ? 'Store it even if its certificate differs from the one it replaces or from the upload key Google Play expects. Users cannot update an installed app signed with another certificate.' : 'Store it even if it does not match the other half yet (upload the matching profile or certificate next).'}</span>
          </Label>
        </div>
      ) : null}
    </>
  );
}

/** The Android keystore's secrets: its password, the key's alias and password, and the optional application id. */
function KeystoreFields({ s }: { s: SigningUploadApi }) {
  const missing = (text: RegExp) => (s.problem && s.file && text.test(s.problem) ? s.problem : undefined);
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField id="signing-keystore-password" label="Keystore password" required error={missing(/keystore password/i)}>
          <Input id="signing-keystore-password" data-testid="signing-keystore-password" type="password" autoComplete="off" value={s.password} onChange={(e) => s.setPassword(e.target.value)} />
        </FormField>
        <FormField id="signing-key-alias" label="Key alias" required error={missing(/key alias/i)}>
          <Input id="signing-key-alias" data-testid="signing-key-alias" value={s.keyAlias} onChange={(e) => s.setKeyAlias(e.target.value)} className="font-mono sm:text-[13px]" autoComplete="off" spellCheck={false} />
        </FormField>
      </div>
      <FormField id="signing-key-password" label="Key password" hint="Leave empty when it is the same as the keystore password (the default for PKCS#12).">
        <Input id="signing-key-password" data-testid="signing-key-password" type="password" autoComplete="off" value={s.keyPassword} onChange={(e) => s.setKeyPassword(e.target.value)} />
      </FormField>
      <FormField id="signing-application-id" label="Application id" error={missing(/application id/i)} hint="Optional: the package name, only needed when the app has no Google Play store app registered in Hangar.">
        <Input id="signing-application-id" data-testid="signing-application-id" value={s.applicationId} onChange={(e) => s.setApplicationId(e.target.value)} placeholder="com.client.app" className="font-mono sm:text-[13px]" autoComplete="off" spellCheck={false} />
      </FormField>
    </>
  );
}
